import { Box, Grid, makeStyles, Paper } from '@material-ui/core';
import grey from '@material-ui/core/colors/grey';
import { useObserver } from 'mobx-react-lite';
import React, { Fragment, FC } from 'react';
import OrderDetailArea from './OrderDetail/OrderDetailArea';
import OrderDetailDialog from './OrderDetail/OrderDetailDialog';
import InfoDialog from './InfoDialog/InfoDialog';
import OrderingInteraction from './OrderingInteraction/OrderingInteraction';
import { OrderingStore } from '../../stores/OrderingStore';
import { LayoutVariant } from '../Core/CommonTypes';
import EditOrderItem from './EditOrderItem/EditOrderItem';
import { isNullOrUndefined } from 'util';
import { KioskOrderingStore, OnlineOrderingStore } from 'kiolyn-api';
import OrderInfoLandingPage from './InfoDialog/OrderInfoLandingPage';
import AuthDialog from '../Auth/AuthDialog/AuthDialog';
import IdleTime, { TypeIdleTime } from '../App/IdleTime';
import TableTent from './TableTent/TableTen';

const useStyles = makeStyles(theme => ({
  orderingContainer: {
    height: '100%',
    flexWrap: 'nowrap'
  },
  bizHour: {
    marginBottom: 2
  },
  orderDetail: {
    minHeight: 480,
    padding: theme.spacing(1),
    backgroundColor: grey[50],
    minWidth: theme.spacing(42),
    flex: '1 1 auto'
  },
  orderingInteraction: {
    paddingTop: theme.spacing(1) / 2
  },
  orderingDetail: {
    flexWrap: 'nowrap',
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

interface OrderingProps {
  orderingStore: OrderingStore;
  acceptOrdering?: boolean;
  defaultView?: boolean;
  showOrderDetailDialog: boolean;
  showInfoDialog: boolean;
  showLoginDialog: boolean;
  showInfoOrderDialog: boolean;
  onSubmit?: () => void;
  onClose?: () => void;
  onClick?: () => void;
  variant?: LayoutVariant;
}

const Ordering: FC<OrderingProps> = ({
  orderingStore,
  acceptOrdering,
  defaultView,
  showOrderDetailDialog,
  showInfoDialog,
  showLoginDialog,
  showInfoOrderDialog,
  onSubmit,
  onClose,
  onClick,
  variant
}: OrderingProps) => {
  const classes = useStyles();

  const menu = useObserver(() => (
    <OrderingInteraction
      showCategoryTypes={orderingStore.store.show_category_types}
      categories={orderingStore.categories}
      allCategoryTypes={orderingStore.store.category_types}
      selectItem={(cat, item) => orderingStore.createItem(cat, item)}
      variant={variant}
      acceptOrdering={acceptOrdering}
      defaultView={defaultView}
      order={orderingStore.order}
      delayTime={orderingStore.getOrderDelayTime()}
      deliveryTime={orderingStore.getOrderDeliveryTime()}
    />
  ));

  const editOrderItemDialog = useObserver(
    () =>
      !isNullOrUndefined(orderingStore.editingOrderItem) && (
        <EditOrderItem
          orderItem={orderingStore.editingOrderItem}
          allModifiers={orderingStore.editingModifiers}
          onClose={() => orderingStore.finishEditing(false)}
          onSave={() => orderingStore.finishEditing(true)}
          variant={variant}
          showSpecialNote={
            variant === LayoutVariant.kiosk
              ? (orderingStore.store as KioskOrderingStore)
                  .kiosk_landing_page_settings?.special_note
              : orderingStore.store.show_special_note
          }
        />
      )
  );

  const cartDialog = useObserver(() => (
    <OrderDetailDialog onClose={onClose} onSubmit={onSubmit} />
  ));

  const infoDialog = useObserver(() => (
    <InfoDialog onClose={onClose} onClick={onClick} variant={variant} />
  ));

  const orderInfoLandingPage = useObserver(() => (
    <OrderInfoLandingPage onClose={onClose} variant={variant} />
  ));

  const tableTentDialog = useObserver(() => <TableTent variant={variant} />);

  const store = orderingStore.store as OnlineOrderingStore;
  const typeKiosk = new URLSearchParams(window.location.search).get('type');
  const isEnableTableTent =
    variant === LayoutVariant.kiosk &&
    store.kiosk_landing_page_settings?.enable_table_tent_prompt &&
    typeKiosk === 'dinein';

  return (
    <Fragment>
      {variant === LayoutVariant.mobile || !acceptOrdering ? (
        menu
      ) : (
        <Grid container className={classes.orderingContainer}>
          <Grid key="ordering" item xs={8}>
            <Box className={classes.orderingInteraction}>{menu}</Box>
          </Grid>
          <Grid
            key="detail"
            item
            xs={4}
            container
            direction="column"
            className={classes.orderingDetail}
          >
            <Paper className={classes.orderDetail}>
              <OrderDetailArea onSubmit={onSubmit} variant={variant} />
            </Paper>
          </Grid>
        </Grid>
      )}
      {orderingStore.editingOrderItem && editOrderItemDialog}
      {showOrderDetailDialog && cartDialog}
      {store.info_page_settings?.enable_info_page &&
        showInfoDialog &&
        infoDialog}
      <AuthDialog open={showLoginDialog} onClose={onClose} variant={variant} />
      {!showInfoDialog && <IdleTime type={TypeIdleTime.Web} />}
      {showInfoOrderDialog && orderInfoLandingPage}
      {isEnableTableTent && tableTentDialog}
    </Fragment>
  );
};

export default Ordering;
